import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingCart, Search, Terminal, Menu } from "lucide-react";
import { cartCount } from "../lib/cart";
import api from "../lib/api";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

const NAV_LINKS = [
  { to: "/products", label: "products", testid: "nav-products" },
  { to: "/about", label: "about", testid: "nav-about" },
  { to: "/faq", label: "faq", testid: "nav-faq" },
  { to: "/contact", label: "contact", testid: "nav-contact" },
  { to: "/order-lookup", label: "orders", testid: "nav-order-lookup" },
];

export default function Header() {
  const [count, setCount] = useState(cartCount());
  const [settings, setSettings] = useState({ site_name: "genuine.software", announcement_text: "", announcement_active: true });
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const upd = () => setCount(cartCount());
    window.addEventListener("cart_updated", upd);
    window.addEventListener("storage", upd);
    return () => {
      window.removeEventListener("cart_updated", upd);
      window.removeEventListener("storage", upd);
    };
  }, []);

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  // Close drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [loc.pathname]);

  return (
    <>
      {settings.announcement_active && settings.announcement_text && (
        <div data-testid="announcement-banner" className="border-b border-[#1E293B] bg-[#00FF66]/[0.06] text-[#00FF66] text-center py-2 font-mono text-xs tracking-wide">
          <span className="inline-flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00FF66] animate-pulse" />
            {settings.announcement_text}
          </span>
        </div>
      )}
      <header data-testid="site-header" className="sticky top-0 z-40 backdrop-blur-xl bg-[#0D0D12]/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link data-testid="header-logo" to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md border border-[#00FF66]/40 bg-[#00FF66]/10 flex items-center justify-center">
              <Terminal className="w-4 h-4 text-[#00FF66]" />
            </div>
            <span className="font-heading font-bold text-lg tracking-tight">
              genuine<span className="text-[#00FF66]">.</span>software
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-7 text-sm font-mono">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                data-testid={l.testid}
                to={l.to}
                className={`hover:text-[#00FF66] transition-colors ${loc.pathname.startsWith(l.to) ? "text-[#00FF66]" : "text-[#A1A1AA]"}`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <Link data-testid="nav-search" to="/products" className="p-2 rounded-md hover:bg-white/5 text-[#A1A1AA]">
              <Search className="w-4 h-4" />
            </Link>
            <Link data-testid="nav-cart" to="/checkout" className="relative p-2 rounded-md hover:bg-white/5 text-[#A1A1AA]">
              <ShoppingCart className="w-4 h-4" />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#00FF66] text-black text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center font-mono">
                  {count}
                </span>
              )}
            </Link>

            {/* Mobile hamburger */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="mobile-menu-trigger"
                  aria-label="Open menu"
                  className="md:hidden p-2 rounded-md hover:bg-white/5 text-[#A1A1AA]"
                >
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent
                data-testid="mobile-menu-drawer"
                side="right"
                className="bg-[#0D0D12] border-l border-white/10 text-white w-[78%] sm:max-w-sm p-0"
              >
                <div className="flex items-center gap-2 px-6 h-16 border-b border-white/5">
                  <div className="w-8 h-8 rounded-md border border-[#00FF66]/40 bg-[#00FF66]/10 flex items-center justify-center">
                    <Terminal className="w-4 h-4 text-[#00FF66]" />
                  </div>
                  <span className="font-heading font-bold text-lg tracking-tight">
                    genuine<span className="text-[#00FF66]">.</span>software
                  </span>
                </div>
                <nav className="flex flex-col px-2 py-4 font-mono text-sm">
                  {NAV_LINKS.map((l) => {
                    const active = loc.pathname.startsWith(l.to);
                    return (
                      <Link
                        key={l.to}
                        data-testid={`mobile-${l.testid}`}
                        to={l.to}
                        onClick={() => setMobileOpen(false)}
                        className={`px-4 py-3 rounded-md transition-colors flex items-center justify-between ${
                          active
                            ? "bg-[#00FF66]/10 text-[#00FF66]"
                            : "text-[#A1A1AA] hover:bg-white/5 hover:text-white"
                        }`}
                      >
                        <span>{l.label}</span>
                        {active && <span className="w-1.5 h-1.5 rounded-full bg-[#00FF66]" />}
                      </Link>
                    );
                  })}
                </nav>
                <div className="px-6 mt-2 text-[11px] font-mono text-[#52525B] tracking-wide uppercase">
                  &gt; original licenses, instant delivery
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
    </>
  );
}
